import moment from 'moment';

import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/EditOutlined';
import { IconButton, TableCell, TableRow, Tooltip } from '@mui/material';

import { LienRecovery } from '../../../api/paymentsApi/deductionsApi';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';

interface DeductionRecoveriesTableDataRowProps {
  tableData: LienRecovery[];
  canEdit: boolean;
  onEdit: (recovery: LienRecovery) => void;
  onDownload: (recoveryId: number) => void;
  onDelete: (recoveryId: number) => void;
}

const DeductionRecoveriesTableDataRow = ({
  tableData,
  canEdit,
  onEdit,
  onDownload,
  onDelete,
}: DeductionRecoveriesTableDataRowProps) => {
  const formattedRecoveriesList = tableData.map((recovery) => ({
    recovery,
    rowFields: {
      // recoveredDate is midnight UTC, so format in UTC to keep the selected day
      recoveredDate: moment.utc(recovery.recoveredDate).format('M/D/YYYY'),
      recoveredAmount: numberToUSDCurrency(recovery.recoveredAmount),
      reason: recovery.reason || '-',
      note: recovery.note || '-',
    },
  }));

  return (
    <>
      {formattedRecoveriesList.map((tableItem) => (
        <TableRow key={tableItem.recovery.id}>
          {Object.entries(tableItem.rowFields).map(([key, value], idx) => (
            <TableCell key={idx}>
              {(key === 'reason' || key === 'note') && value !== '-' ? (
                <Tooltip
                  title={value}
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: (theme) => ({
                        backgroundColor: theme.palette.common.white,
                        color: theme.palette.primary.light,
                        borderRadius: '5px',
                        boxShadow: '3px',
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        fontWeight: 500,
                        padding: '5px',
                        maxWidth: '250px',
                      }),
                    },
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {value}
                  </span>
                </Tooltip>
              ) : (
                value
              )}
            </TableCell>
          ))}
          <TableCell>
            {canEdit && (
              <Tooltip title="Edit recovery">
                <IconButton size="small" sx={{ color: 'common.white' }} onClick={() => onEdit(tableItem.recovery)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={tableItem.recovery.documentLink ? 'Download document' : 'No document uploaded'}>
              {/* Wrapper keeps the tooltip working while the button is disabled */}
              <span>
                <IconButton
                  size="small"
                  sx={{ color: 'common.white', '&.Mui-disabled': { color: 'text.disabled' } }}
                  disabled={!tableItem.recovery.documentLink}
                  onClick={() => onDownload(tableItem.recovery.id)}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {canEdit && (
              <Tooltip title="Delete recovery">
                <IconButton
                  size="small"
                  sx={{ color: 'common.white' }}
                  onClick={() => onDelete(tableItem.recovery.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

export default DeductionRecoveriesTableDataRow;
